import { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Linking } from 'react-native';
import { Search, Pencil, Plus } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { Button } from '../common/Button';
import { TextLink } from '../common/TextLink';
import type { ConfirmOptions } from '../common/ConfirmDialog';
import { useAreaContactsStore } from '../../store/areaContactsStore';
import type { AreaContactInput } from '../../api/areaContacts';
import { filterAreaContacts } from '../../utils/areaPeopleMatch';
import { initials } from '../../utils/initials';
import { PLAN_PARTY_TYPES } from './planParties';
import type { AreaContact, PlanPartyType } from '../../types';

// The moderator's area directory (Glenn's ask, Sept 2026): moderator-managed
// name/role/organization/phone/email for players in an area. A PanelStack
// view pushed from the People panel (PeoplePanel.web.tsx), for moderators
// only. It used to be pushed from Assign with a "Use" button per row; Assign
// now picks from the owner's own lists and never shows this directory (Oct
// 2026).
//
// `confirm` is a prop, not `useConfirm()` called here directly, so a host
// that sits above ConfirmProvider (see docs/plans/panel-system.md's "gorhom
// portal sits above providers" gotcha) can pass its own down.

/** "Add a person" as a row under the search, where it's seen before the
 *  list (the prototype's People view), not a button after it. */
function AddPersonRow({ onPress }: { onPress: () => void }) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      testID="people-add"
      accessibilityRole="button"
      accessibilityLabel="Add a person"
      {...hoverProps}
      style={[
        styles.addRow,
        CURSOR_POINTER,
        {
          borderColor: colors.border,
          backgroundColor: hovered ? colors.hoverSurface : 'transparent',
        },
      ]}>
      <Plus size={16} color={colors.textSecondary} />
      <Text style={[styles.addRowLabel, { color: colors.text }]}>Add a person</Text>
    </Pressable>
  );
}

function Chip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole="radio"
      aria-checked={selected}
      accessibilityLabel={label}
      {...hoverProps}
      style={[
        styles.chip,
        CURSOR_POINTER,
        {
          borderColor: selected ? colors.action : colors.border,
          backgroundColor: selected ? colors.action : hovered ? colors.hoverSurface : 'transparent',
        },
      ]}>
      <Text style={[styles.chipLabel, { color: selected ? colors.actionOn : colors.text }]}>
        {label}
      </Text>
    </Pressable>
  );
}

function emptyForm() {
  return {
    name: '',
    organization: '',
    role: '',
    partyType: 'other' as PlanPartyType,
    phone: '',
    email: '',
    notes: '',
  };
}

export function AreaPeopleView({
  areaId,
  confirm,
}: {
  areaId: string | null;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}) {
  const { colors } = useTheme();
  const contacts = useAreaContactsStore((s) => s.contacts);
  const subscribe = useAreaContactsStore((s) => s.subscribe);
  const createContact = useAreaContactsStore((s) => s.create);
  const updateContact = useAreaContactsStore((s) => s.update);
  const removeContact = useAreaContactsStore((s) => s.remove);

  const [mode, setMode] = useState<'list' | 'form'>('list');
  const [editing, setEditing] = useState<AreaContact | null>(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // This view is pushed fresh onto the PanelStack each time (a pop unmounts
  // it — see PanelStack's doc comment), so mount time IS "just opened" —
  // no visible-gated re-seed needed the way the old sheet needed one.
  useEffect(() => {
    if (!areaId) return;
    subscribe(areaId);
  }, [areaId, subscribe]);

  const visibleContacts = useMemo(() => filterAreaContacts(contacts, search), [contacts, search]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setError(null);
    setMode('form');
  };

  const openEdit = (contact: AreaContact) => {
    setEditing(contact);
    setForm({
      name: contact.name,
      organization: contact.organization ?? '',
      role: contact.role ?? '',
      partyType: contact.partyType,
      phone: contact.phone ?? '',
      email: contact.email ?? '',
      notes: contact.notes ?? '',
    });
    setError(null);
    setMode('form');
  };

  const canSave = form.name.trim().length > 0;

  const handleSave = async () => {
    if (!areaId || !canSave) return;
    setSaving(true);
    setError(null);
    const input: AreaContactInput = {
      name: form.name.trim(),
      organization: form.organization.trim() || undefined,
      role: form.role.trim() || undefined,
      partyType: form.partyType,
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      notes: form.notes.trim() || undefined,
    };
    try {
      if (editing) {
        await updateContact(editing.id, input);
      } else {
        const id = await createContact(areaId, input);
        if (!id) {
          setError("Couldn't add that person. Try again.");
          return;
        }
      }
      setMode('list');
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    const ok = await confirm({
      title: 'Remove this person?',
      message: `This removes ${editing.name} from the People list.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    await removeContact(editing.id);
    setMode('list');
    setEditing(null);
  };

  return (
    <View style={styles.content}>
      {mode === 'list' ? (
        <>
          <View style={[styles.searchRow, { borderColor: colors.border }]}>
            <Search size={14} color={colors.textTertiary} />
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search name, organization or role"
              placeholderTextColor={colors.textTertiary}
              testID="people-search"
              accessibilityLabel="Search people"
              style={[styles.searchInput, { color: colors.text }]}
            />
          </View>
          <AddPersonRow onPress={openAdd} />

          {visibleContacts.length === 0 ? (
            <Text style={[styles.empty, { color: colors.textSecondary }]}>
              {contacts.length === 0
                ? "No one here yet. Add the people you work with, and they'll show up for every plan nearby."
                : 'No one matches that search.'}
            </Text>
          ) : (
            <View style={styles.list}>
              {visibleContacts.map((contact) => {
                const phone = contact.phone;
                const email = contact.email;
                const metaLine = [contact.role, contact.organization].filter(Boolean).join(' · ');
                return (
                  <View
                    key={contact.id}
                    style={[styles.row, { borderColor: colors.border }]}
                    testID={`people-row-${contact.id}`}>
                    <View
                      style={[
                        styles.avatar,
                        { backgroundColor: colors.surface, borderColor: colors.border },
                      ]}>
                      <Text style={[styles.avatarText, { color: colors.textSecondary }]}>
                        {initials(contact.name)}
                      </Text>
                    </View>
                    <View style={styles.rowMain}>
                      <Text style={[styles.rowName, { color: colors.text }]} numberOfLines={1}>
                        {contact.name}
                      </Text>
                      {metaLine.length > 0 && (
                        <Text
                          style={[styles.rowMeta, { color: colors.textSecondary }]}
                          numberOfLines={1}>
                          {metaLine}
                        </Text>
                      )}
                      {(phone || email) && (
                        <View style={styles.rowLinks}>
                          {phone && (
                            <TextLink
                              label={phone}
                              onPress={() => void Linking.openURL(`tel:${phone}`).catch(() => {})}
                              textStyle={styles.rowLinkText}
                              testID={`people-phone-${contact.id}`}
                              accessibilityLabel={`Call ${contact.name}`}
                            />
                          )}
                          {email && (
                            <TextLink
                              label={email}
                              onPress={() =>
                                void Linking.openURL(`mailto:${email}`).catch(() => {})
                              }
                              textStyle={styles.rowLinkText}
                              testID={`people-email-${contact.id}`}
                              accessibilityLabel={`Email ${contact.name}`}
                            />
                          )}
                        </View>
                      )}
                    </View>
                    <View style={styles.rowActions}>
                      <Pressable
                        onPress={() => openEdit(contact)}
                        testID={`people-edit-${contact.id}`}
                        accessibilityRole="button"
                        accessibilityLabel={`Edit ${contact.name}`}
                        style={[CURSOR_POINTER, styles.iconButton]}>
                        <Pencil size={16} color={colors.textSecondary} />
                      </Pressable>
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </>
      ) : (
        <>
          <Text style={[styles.formTitle, { color: colors.text }]}>
            {editing ? 'Edit person' : 'Add person'}
          </Text>

          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Name</Text>
          <TextInput
            value={form.name}
            onChangeText={(name) => setForm((f) => ({ ...f, name }))}
            placeholder="e.g. Maria Lopez"
            placeholderTextColor={colors.textTertiary}
            testID="people-form-name"
            accessibilityLabel="Name"
            style={[styles.input, { color: colors.text, borderColor: colors.border }]}
          />
          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Organization</Text>
          <TextInput
            value={form.organization}
            onChangeText={(organization) => setForm((f) => ({ ...f, organization }))}
            placeholder="e.g. Big Bear Fire Safe Council"
            placeholderTextColor={colors.textTertiary}
            testID="people-form-organization"
            accessibilityLabel="Organization"
            style={[styles.input, { color: colors.text, borderColor: colors.border }]}
          />
          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Role</Text>
          <TextInput
            value={form.role}
            onChangeText={(role) => setForm((f) => ({ ...f, role }))}
            placeholder="e.g. Battalion Chief"
            placeholderTextColor={colors.textTertiary}
            testID="people-form-role"
            accessibilityLabel="Role"
            style={[styles.input, { color: colors.text, borderColor: colors.border }]}
          />

          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Type</Text>
          <View style={styles.typeChips}>
            {PLAN_PARTY_TYPES.map((t) => (
              <Chip
                key={t.key}
                label={t.label}
                selected={form.partyType === t.key}
                onPress={() => setForm((f) => ({ ...f, partyType: t.key }))}
                testID={`people-form-type-${t.key}`}
              />
            ))}
          </View>

          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Phone</Text>
          <TextInput
            value={form.phone}
            onChangeText={(phone) => setForm((f) => ({ ...f, phone }))}
            placeholder="e.g. (909) 555-0142"
            placeholderTextColor={colors.textTertiary}
            keyboardType="phone-pad"
            testID="people-form-phone"
            accessibilityLabel="Phone"
            style={[styles.input, { color: colors.text, borderColor: colors.border }]}
          />
          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Email</Text>
          <TextInput
            value={form.email}
            onChangeText={(email) => setForm((f) => ({ ...f, email }))}
            placeholderTextColor={colors.textTertiary}
            keyboardType="email-address"
            autoCapitalize="none"
            testID="people-form-email"
            accessibilityLabel="Email"
            style={[styles.input, { color: colors.text, borderColor: colors.border }]}
          />
          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Notes</Text>
          <TextInput
            value={form.notes}
            onChangeText={(notes) => setForm((f) => ({ ...f, notes }))}
            placeholder="e.g. Best reached in the morning"
            placeholderTextColor={colors.textTertiary}
            multiline
            maxLength={500}
            testID="people-form-notes"
            accessibilityLabel="Notes"
            style={[
              styles.input,
              styles.notesInput,
              { color: colors.text, borderColor: colors.border },
            ]}
          />

          {error && <Text style={[styles.error, { color: colors.danger }]}>{error}</Text>}

          {/* Same footer as Assign and the plan panel (Sept 28): Cancel and
              Save as two equal sm buttons. Delete, when editing, sits on its
              own line above so it can't be mistaken for either. */}
          {editing && (
            <View style={styles.deleteRow}>
              <Button
                title="Delete"
                onPress={() => void handleDelete()}
                variant="danger"
                size="sm"
                testID="people-form-delete"
              />
            </View>
          )}
          <View style={styles.formActions}>
            <Button
              title="Cancel"
              onPress={() => setMode('list')}
              variant="secondary"
              size="sm"
              testID="people-form-cancel"
              style={styles.formAction}
            />
            <Button
              title="Save"
              onPress={() => void handleSave()}
              variant="primary"
              size="sm"
              disabled={!canSave}
              loading={saving}
              testID="people-form-save"
              style={styles.formAction}
            />
          </View>
        </>
      )}

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="area-people-sheet-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  // Same side padding as the drill-in heading above it (PanelStack) and
  // the companion header, so everything shares one left edge.
  content: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
    paddingBottom: SPACING.xxl,
  },
  formTitle: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.md,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    marginBottom: SPACING.sm,
  },
  searchInput: {
    ...TYPOGRAPHY.body,
    flex: 1,
    paddingVertical: SPACING.sm,
  },
  empty: {
    ...TYPOGRAPHY.body,
    marginBottom: SPACING.md,
  },
  list: {
    marginBottom: SPACING.md,
  },
  // Rows sit on hairlines, not in boxes (the prototype's list).
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: SPACING.smd,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    ...withWeight(TYPOGRAPHY.caption, 'semibold'),
  },
  addRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.smd,
    marginBottom: SPACING.xs,
  },
  addRowLabel: {
    ...TYPOGRAPHY.label,
  },
  rowMain: {
    flex: 1,
    gap: SPACING.xxs,
  },
  rowName: {
    ...TYPOGRAPHY.label,
  },
  rowMeta: {
    ...TYPOGRAPHY.caption,
  },
  rowLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
    marginTop: SPACING.xxs,
  },
  rowLinkText: {
    ...TYPOGRAPHY.caption,
  },
  rowActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  iconButton: {
    padding: SPACING.xxs,
  },
  // Field labels as in the Assign form: small, sentence case.
  sectionLabel: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
    marginTop: SPACING.xs,
    marginBottom: SPACING.xs,
  },
  typeChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.xs,
    marginBottom: SPACING.xs,
  },
  chip: {
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xxs,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
  },
  chipLabel: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
  },
  input: {
    ...TYPOGRAPHY.bodySmall,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    marginBottom: SPACING.xs,
  },
  notesInput: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  error: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.xs,
  },
  deleteRow: {
    alignItems: 'flex-start',
    marginTop: SPACING.md,
  },
  formActions: {
    flexDirection: 'row',
    gap: SPACING.sm,
    marginTop: SPACING.md,
  },
  formAction: {
    flex: 1,
  },
  scrollSentinel: {
    height: 1,
  },
});
